import React, { useState, useEffect } from 'react';
import { ChevronRight, Shield, Clock, Zap, Users, BarChart3, Eye, CheckCircle } from 'lucide-react';
import Header from "./header";
import Footer from './footer';

const Base24ServicesPage = () => {
  const [visibleSections, setVisibleSections] = useState({});
  const [hoveredCard, setHoveredCard] = useState(null);
  const [expandedCards, setExpandedCards] = useState({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll('.animate-section');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const toggleCard = (cardId) => {
    console.log('Toggling card:', cardId);
    setExpandedCards(prev => {
      const newState = {
        ...prev,
        [cardId]: !prev[cardId]
      };
      console.log('New expanded state:', newState);
      return newState;
    });
  };

  const ServiceCategory = ({ id, icon: Icon, title, description, services, delay = 0, colorScheme }) => {
    const isExpanded = expandedCards[id] || false;
    const hasServices = services && services.length > 0;
    
    console.log(`Card ${id}: isExpanded=${isExpanded}, hasServices=${hasServices}`);
    
    return (
      <div 
        className={`bg-gradient-to-br ${colorScheme.background} rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 border-2 ${colorScheme.border} overflow-hidden opacity-0 animate-fade-in-up transform hover:scale-105`}
        style={{ animationDelay: `${delay}ms` }}
      >
        <div className="p-6">
          <div className={`flex items-center justify-center w-16 h-16 bg-gradient-to-br ${colorScheme.iconBg} rounded-xl mb-4 shadow-lg`}>
            <Icon className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-3">{title}</h3>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">{description}</p>
          
          {hasServices && (
            <button
              onClick={() => toggleCard(id)}
              className={`flex items-center gap-2 ${colorScheme.buttonText} hover:${colorScheme.buttonHover} font-medium transition-colors`}
            >
              {isExpanded ? 'Show Less' : 'Learn More'}
              <ChevronRight className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? 'rotate-90' : ''}`} />
            </button>
          )}
        </div>
        
        {isExpanded && hasServices && (
          <div className="transition-all duration-500 ease-in-out">
            <div className={`px-6 pb-6 border-t-2 ${colorScheme.border}`}>
              <div className="pt-4 space-y-4">
                {services.map((service, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-white/50 rounded-lg backdrop-blur-sm">
                    <div className={`w-2 h-2 ${colorScheme.dot} rounded-full mt-2 flex-shrink-0`}></div>
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-1">{service.title}</h4>
                      <p className="text-gray-600 text-sm">{service.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const FeatureCard = ({ icon: Icon, title, description, delay = 0 }) => (
    <div 
      className={`text-center p-6 bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 opacity-0 animate-fade-in-up`}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full mb-4 mx-auto">
        <Icon className="w-8 h-8 text-white" />
      </div>
      <h3 className="text-xl font-bold text-gray-800 mb-2">{title}</h3>
      <p className="text-gray-600 text-sm">{description}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50">
      {/* Header */}
   <Header/>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 to-cyan-600/5"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="opacity-0 animate-fade-in-left">
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
                <span className="text-blue-600">Base24</span> Implementation &{' '}
                <span className="text-gray-800">Support Services</span>
              </h1>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                At Anvitech Technologies, we specialize in Base24 implementation, 
                integration, and support services. Our expert team helps financial 
                institutions deploy robust payment processing solutions that enhance 
                security, scalability, and performance.
              </p>
           
            </div>
            <div className="opacity-0 animate-fade-in-right">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-2xl blur opacity-20 animate-pulse"></div>
                <div className="relative bg-white rounded-2xl p-8 shadow-2xl">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Shield className="w-8 h-8 text-blue-600" />
                      </div>
                      <h3 className="font-semibold text-gray-800">Secure</h3>
                      <p className="text-sm text-gray-600">Enterprise-grade security</p>
                    </div>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Zap className="w-8 h-8 text-green-600" />
                      </div>
                      <h3 className="font-semibold text-gray-800">Fast</h3>
                      <p className="text-sm text-gray-600">High-performance processing</p>
                    </div>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Users className="w-8 h-8 text-purple-600" />
                      </div>
                      <h3 className="font-semibold text-gray-800">Scalable</h3>
                      <p className="text-sm text-gray-600">Grows with your business</p>
                    </div>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Clock className="w-8 h-8 text-orange-600" />
                      </div>
                      <h3 className="font-semibold text-gray-800">24/7</h3>
                      <p className="text-sm text-gray-600">Round-the-clock support</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 animate-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Our <span className="text-blue-600">Base24</span> Services
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Comprehensive Base24 solutions designed to transform your payment processing infrastructure
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <ServiceCategory 
              id="implementation"
              icon={Zap}
              title="Base24 Implementation & Migration"
              description="Complete end-to-end Base24 deployment with seamless migration from legacy systems."
              delay={0}
              colorScheme={{
                background: 'from-blue-50 to-indigo-100',
                border: 'border-blue-200',
                iconBg: 'from-blue-500 to-indigo-600',
                buttonText: 'text-blue-600',
                buttonHover: 'text-blue-700',
                dot: 'bg-blue-500'
              }}
              services={[
                {
                  title: "End-to-End Development",
                  description: "Complete Base24 implementation from planning to deployment with comprehensive testing and validation."
                },
                {
                  title: "Seamless Migration",
                  description: "Smooth transition from legacy systems to Base24 with minimal downtime and zero data loss."
                },
                {
                  title: "Customization & Configuration",
                  description: "Tailored Base24 configurations to meet your specific business requirements and compliance standards."
                }
              ]}
            />

            <ServiceCategory 
              id="integration"
              icon={Users}
              title="Payment Ecosystem Integration"
              description="Seamless integration with card networks, banking systems, and terminal infrastructure."
              delay={200}
              colorScheme={{
                background: 'from-emerald-50 to-teal-100',
                border: 'border-emerald-200',
                iconBg: 'from-emerald-500 to-teal-600',
                buttonText: 'text-emerald-600',
                buttonHover: 'text-emerald-700',
                dot: 'bg-emerald-500'
              }}
              services={[
                {
                  title: "Card Networks & Schemes",
                  description: "Integration with Visa, MasterCard, American Express, and other major payment networks worldwide."
                },
                {
                  title: "Core Banking Connectivity",
                  description: "Real-time connection to core banking systems for instant transaction processing and account management."
                },
                {
                  title: "ATM & POS Terminal Integration",
                  description: "Complete terminal management supporting various ATM manufacturers and POS configurations."
                }
              ]}
            />

            <ServiceCategory 
              id="support"
              icon={Clock}
              title="24/7 Support & Maintenance"
              description="Round-the-clock monitoring, support, and maintenance services for optimal system performance."
              delay={400}
              colorScheme={{
                background: 'from-purple-50 to-violet-100',
                border: 'border-purple-200',
                iconBg: 'from-purple-500 to-violet-600',
                buttonText: 'text-purple-600',
                buttonHover: 'text-purple-700',
                dot: 'bg-purple-500'
              }}
              services={[
                {
                  title: "Real-time Monitoring & Issue Resolution",
                  description: "Continuous system monitoring with proactive issue detection and rapid resolution capabilities."
                },
                {
                  title: "Performance Optimization",
                  description: "Ongoing performance tuning and optimization to ensure peak system efficiency and reliability."
                },
                {
                  title: "Security & Compliance Updates",
                  description: "Regular security patches and compliance updates to meet evolving regulatory requirements."
                }
              ]}
            />

            <ServiceCategory 
              id="fraud"
              icon={Shield}
              title="Fraud Prevention & Risk Management"
              description="Advanced fraud detection and risk management solutions to protect your transactions."
              delay={600}
              colorScheme={{
                background: 'from-orange-50 to-red-100',
                border: 'border-orange-200',
                iconBg: 'from-orange-500 to-red-600',
                buttonText: 'text-orange-600',
                buttonHover: 'text-orange-700',
                dot: 'bg-orange-500'
              }}
              services={[
                {
                  title: "AI-Powered Fraud Detection",
                  description: "Machine learning algorithms for real-time fraud detection and prevention with minimal false positives."
                },
                {
                  title: "Transaction Monitoring",
                  description: "Comprehensive transaction monitoring with customizable rules and intelligent alert systems."
                },
                {
                  title: "Regulatory Compliance Support",
                  description: "Ensure compliance with PCI-DSS, EMV standards, and other regulatory requirements globally."
                }
              ]}
            />
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section id="why-choose" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50 animate-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Why Choose <span className="text-blue-600">Anvitech Technologies?</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <FeatureCard 
              icon={Users}
              title="Expert Team"
              description="Certified professionals with extensive Base24 experience and proven track record."
              delay={0}
            />
            <FeatureCard 
              icon={Shield}
              title="Secure & Scalable Solutions"
              description="Enterprise-grade security with scalable architecture designed for growth."
              delay={200}
            />
            <FeatureCard 
              icon={Clock}
              title="Minimized Downtime"
              description="Streamlined implementation process with minimal disruption to your operations."
              delay={400}
            />
            <FeatureCard 
              icon={BarChart3}
              title="Proven Track Record"
              description="Successful implementations across various financial institutions worldwide."
              delay={600}
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-cyan-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Payment Processing?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Let Anvitech Technologies help you build a robust, secure, and scalable Base24 
            payment processing solution that drives your business forward.
          </p>
      

        </div>
        
      </section>
<Footer/>
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes fadeInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .animate-fade-in-up {
          animation: fadeInUp 0.8s ease-out forwards;
        }

        .animate-fade-in-left {
          animation: fadeInLeft 1s ease-out forwards;
        }

        .animate-fade-in-right {
          animation: fadeInRight 1s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default Base24ServicesPage;